import { getContactSupport } from '@cosign/utils/getContactSupport'
import { useApiGetOrderPdfUrl } from '@hooks-api/eCommerceV2/useApiGetOrderPdfUrl'
import { type ChildOrder } from '@hooks-api/types/dataModel/fpOrder'
import { Button } from '@pets-ui/Button/Button'
import { Chip } from '@pets-ui/Chip/Chip'
import { HStack } from '@pets-ui/HStack/HStack'
import { Download, ErrorFilled } from '@pets-ui/icons'
import { Paper } from '@pets-ui/Paper/Paper'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { formatPrice } from '@utils'
import { useTranslation } from 'react-i18next'
import { useContextOrderDetail } from '../shared/OrderDetailProvider/OrderDetailProvider'
import { OrderInfo } from '../shared/Orders/OrderInfo'
import { ShipmentOrderFastpass } from './ShipmentOrderFastpass'

const styles = stylex.create({
  orderPdf: {
    padding: 12,
    borderRadius: 0,
    backgroundColor: colors.blueGrey500,
  },
})

interface OrderFastpassProps {
  order: ChildOrder
}

export function OrderFastpass({ order }: OrderFastpassProps) {
  const { t } = useTranslation()
  const { showPdf: shouldShowPdf, currency, transaction, countryCode } = useContextOrderDetail()
  const { id, order_id, total, project_code, is_donation, is_pre_order, subship_info, order_items, shipping_label_providers } = order
  const { contactLink, openContact } = getContactSupport(project_code)

  const { trigger: getOrderPdfUrl, isMutating: isPdfLoading } = useApiGetOrderPdfUrl()
  const { openSnackbar } = useSnackbar()

  const showPdfErrorSnackbar = () => {
    openSnackbar({
      message: t('orderDetail.orderPdfNotReady', { defaultValue: '揀貨單建立中，請稍後再試' }),
      offsets: { top: '100px' },
      anchorOrigin: { horizontal: 'center', vertical: 'top' },
      icon: <ErrorFilled color={colors.red600} />,
    })
  }

  const handleOpenOrderPdf = async () => {
    if (!countryCode || !transaction?.transaction_id || !id) {
      console.error('[handleOpenOrderPdf] Missing required params:', { countryCode, transaction_id: transaction?.transaction_id, id })
      showPdfErrorSnackbar()
      return
    }
    try {
      const response = await getOrderPdfUrl({
        country_code: countryCode,
        payment_transaction_id: transaction.transaction_id,
        order_id: id,
      })
      const pdfUrl = response.data?.data?.order_pdf_url
      if (pdfUrl) {
        window.open(pdfUrl, '_blank')
      }
      else {
        console.error('[handleOpenOrderPdf] PDF URL is empty')
        showPdfErrorSnackbar()
      }
    }
    catch (error) {
      console.error('[handleOpenOrderPdf] Failed to fetch PDF URL:', error)
      showPdfErrorSnackbar()
    }
  }

  return (
    <Paper borderRadius={8} overflow="hidden" elevation={0}>
      <HStack width="100%">
        <HStack justifyContent="space-between" p={3} backgroundColor={colors.blueGrey500} flexGrow={1}>
          <HStack gap={1} alignItems="center">
            <Text color={colors.white}>{`${t('orderDetail.orderNumberLabel', { defaultValue: '訂單編號：' })}${order_id}`}</Text>
          </HStack>
          {is_donation && <Chip variant="square" size="small" color="red">{t('orderDetail.charity', { defaultValue: '公益' })}</Chip>}
          {is_pre_order && <Chip variant="square" size="small" color="red">{t('orderDetail.preOrder', { defaultValue: '預購' })}</Chip>}
        </HStack>
        {shouldShowPdf
        && (
          <Button variant="text" rootStyle={styles.orderPdf} onClick={handleOpenOrderPdf} loading={isPdfLoading} disabled={isPdfLoading}>
            <Text variant="body" color={colors.white}>{t('orderDetail.orderPdf', { defaultValue: '訂單明細 PDF' })}</Text>
            <Download fontSize={20} color={colors.white} />
          </Button>
        )}
      </HStack>
      <VStack p={3} gap={3} borderWidth={1} borderColor={colors.grey300}>
        <VStack gap={3}>
          <ShipmentOrderFastpass shippingLabelProviders={shipping_label_providers} subshipType={subship_info?.type} orderItems={order_items} />
        </VStack>
      </VStack>
      <OrderInfo order={order} />
      <HStack justifyContent="space-between" p={3} width="100%" borderWidth="1px 0 0 0" borderColor={colors.grey300} borderStyle="solid">
        <Button
          variant="outlined"
          color="grey"
          size="small"
          onClick={openContact}
          disabled={!contactLink}
        >
          {t('orderDetail.contactService', { defaultValue: '聯絡客服' })}
        </Button>
        <HStack gap={1} alignItems="center">
          <Text variant="body">
            {t('orderDetail.orderTotalLabel', { defaultValue: '訂單總額：' })}
          </Text>
          <Text variant="subtitle">
            {formatPrice(currency, total)}
          </Text>
        </HStack>
      </HStack>
    </Paper>
  )
}
